import { Injectable } from '@angular/core';
import { BehaviorSubject } from 'rxjs';
import { SafeStorageService } from './safe-storage.service';

export interface NotificationPreferences {
  soundEnabled: boolean;
  volume: number; // 0-1
  vibrationEnabled: boolean;
}

@Injectable({
  providedIn: 'root'
})
export class NotificationPreferencesService {
  private readonly STORAGE_KEY = 'notification_preferences';
  
  private defaultPreferences: NotificationPreferences = {
    soundEnabled: true,
    volume: 0.5,
    vibrationEnabled: true
  };

  private preferencesSubject = new BehaviorSubject<NotificationPreferences>(
    this.loadPreferences()
  );

  public preferences$ = this.preferencesSubject.asObservable();

  constructor(private safeStorage: SafeStorageService) {}

  private loadPreferences(): NotificationPreferences {
    const stored = this.safeStorage.getJson<NotificationPreferences>(localStorage, this.STORAGE_KEY);
    if (stored) {
      return { ...this.defaultPreferences, ...stored };
    }
    return this.defaultPreferences;
  }

  private savePreferences(preferences: NotificationPreferences): void {
    this.safeStorage.setJson(localStorage, this.STORAGE_KEY, preferences);
    this.preferencesSubject.next(preferences);
  }

  getPreferences(): NotificationPreferences {
    return this.preferencesSubject.value;
  }

  updatePreferences(preferences: Partial<NotificationPreferences>): void {
    const current = this.getPreferences();
    const updated = { ...current, ...preferences };
    this.savePreferences(updated);
  }

  toggleSound(): void {
    const current = this.getPreferences();
    this.updatePreferences({ soundEnabled: !current.soundEnabled });
  }

  setVolume(volume: number): void {
    // Ensure volume is between 0 and 1
    const normalizedVolume = Math.max(0, Math.min(1, volume));
    this.updatePreferences({ volume: normalizedVolume });
  }

  toggleVibration(): void {
    const current = this.getPreferences();
    this.updatePreferences({ vibrationEnabled: !current.vibrationEnabled });
  }

  isSoundEnabled(): boolean {
    return this.getPreferences().soundEnabled;
  }

  getVolume(): number {
    return this.getPreferences().volume;
  }

  isVibrationEnabled(): boolean {
    return this.getPreferences().vibrationEnabled;
  }
}

